import React, { useCallback } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";
import Select, { type OnChangeValue } from "react-select";

import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { shuffle } from "lodash";

import { Counter, Toggle } from "~/auth/components";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button, colors, Tooltip } from "~/ui";
import { Input, Link } from "~/ui/legacyFellowIndex";

import { JourneyContext } from "./JourneyContext";

import type {
	SyncPageMutation$variables,
	SyncPageMutation as SyncPageMutationType,
} from "~/__generated__/SyncPageMutation.graphql";
import type { SyncPageQuery as QueryType } from "~/__generated__/SyncPageQuery.graphql";
import type { SyncPageRefreshQuery as SyncPageRefreshQueryType } from "~/__generated__/SyncPageRefreshQuery.graphql";

import stylesOnboarding from "~/onboarding/css/Onboarding.module.css";

import styles from "./css/SyncPage.module.css";

export const SYNC_PATH = "/o/sync/";

const SyncPageRefreshQuery = graphql`
	query SyncPageRefreshQuery {
		viewer {
			syncReady
		}
	}
`;

const SyncPageMutation = graphql`
	mutation SyncPageMutation($input: SyncPageSetupMutationInput!) {
		syncPageSetup(input: $input) {
			user {
				id
			}
		}
	}
`;

const OPTIONS = shuffle([
	{ value: "social", label: "Social media" },
	{ value: "search", label: "Search engine" },
	{ value: "podcast", label: "Podcast" },
	{ value: "blog", label: "Blog post" },
	{ value: "friend", label: "From a friend or colleague" },
]).concat({ value: "other", label: "Other..." });

type OptionType = (typeof OPTIONS)[0];

export default function SyncPage() {
	const { nextStep } = React.useContext(JourneyContext);

	const { viewer, user, account } = useLazyLoadQuery<QueryType>(
		graphql`
			query SyncPageQuery {
				viewer {
					questionAnswers
				}
				user {
					firstName
					fullName
					internalAttributes {
						isAccountCreator
						receivedInvites {
							edges {
								node {
									id
								}
							}
						}
					}
				}
				account {
					name
					type
				}
			}
		`,
		{},
		{ fetchPolicy: "store-or-network" },
	);

	const [isManager, setIsManager] = React.useState(
		Boolean(viewer.questionAnswers.is_manager || user.internalAttributes?.isAccountCreator),
	);
	const [numReports, setNumReports] = React.useState(viewer.questionAnswers.number_of_reports ?? 3);
	const [businessUse, setBusinessUse] = React.useState(viewer.questionAnswers.business_use ?? false);
	const [emailOptin, setEmailOptin] = React.useState(true);
	const [userOrigin, setUserOrigin] = React.useState<OptionType | undefined | null>(null);
	const [otherOrigin, setOtherOrigin] = React.useState("");
	const [acceptedTermsOfService, setAcceptedTermsOfService] = React.useState<boolean>(
		viewer.questionAnswers.accepted_terms_of_service ?? false,
	);

	const [profileSaved, setProfileSaved] = React.useState(false);
	const refreshStateTimer = React.useRef(0);

	// Ask Alpha and Gamma users how they heard about Fellow
	const shouldAskOrigin =
		user.internalAttributes?.isAccountCreator || !user.internalAttributes?.receivedInvites?.edges.length;

	const saveProfile = React.useCallback(() => {
		let input: SyncPageMutation$variables["input"] =
			account.type === "PERSONAL" ? { businessUse, emailOptin } : { isManager, numReports, emailOptin };

		if (userOrigin && shouldAskOrigin) {
			input = { ...input, userOrigin: userOrigin.value, otherOrigin };
			delete input.numReports;
		}

		input = { ...input, acceptedTermsOfService };

		commitMutation<SyncPageMutationType>(environment, {
			mutation: SyncPageMutation,
			variables: { input },
			onCompleted: () => {
				setProfileSaved(true);

				let syncAttempts = 0;
				function onNext() {
					window.clearTimeout(refreshStateTimer.current);
					nextStep();
				}

				function refreshState() {
					fetchQuery<SyncPageRefreshQueryType>(environment, SyncPageRefreshQuery, {})
						.toPromise()
						.then(resp => {
							if (!resp) return onNext();
							const { viewer } = resp;
							if (viewer.syncReady || syncAttempts > 30) return onNext();

							refreshStateTimer.current = window.setTimeout(refreshState, 1000);
							syncAttempts += 1;
						})
						.catch(onNext);
				}

				refreshStateTimer.current = window.setTimeout(refreshState, 500);
			},
		});
	}, [
		account.type,
		emailOptin,
		isManager,
		numReports,
		businessUse,
		userOrigin,
		otherOrigin,
		acceptedTermsOfService,
		shouldAskOrigin,
		nextStep,
	]);

	React.useEffect(() => {
		if (window.Cypress) setTimeout(nextStep, 200);
	}, [nextStep]);

	const onIsManagerChange = useCallback((checked: boolean) => {
		setIsManager(checked);
	}, []);

	const onUserOriginChange = useCallback((value: OnChangeValue<OptionType, false>) => {
		setUserOrigin(value);
	}, []);

	const onNumberOfReportsChange = useCallback((count: number) => {
		setNumReports(count);
	}, []);

	const onSignupForWorkChange = useCallback((checked: boolean) => {
		setBusinessUse(checked);
	}, []);

	const onEmailOptinChange = useCallback((checked: boolean) => {
		setEmailOptin(checked);
	}, []);

	const onAcceptedTermsOfServiceChange = useCallback((checked: boolean) => {
		setAcceptedTermsOfService(checked);
	}, []);

	const onOtherOriginChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setOtherOrigin(event.target.value);
	}, []);

	function renderAskOrigin() {
		return (
			<div className={styles.formColumn}>
				<label>How did you hear about Fellow?</label>
				<Select<OptionType, false>
					value={userOrigin}
					options={OPTIONS}
					onChange={onUserOriginChange}
					styles={{
						control: provided => ({
							...provided,
							borderColor: `${colors.grey[700]} !important`,
						}),
						indicatorSeparator: provided => ({
							...provided,
							backgroundColor: `${colors.grey[700]} !important`,
						}),
						valueContainer: provided => ({
							...provided,
							padding: "4px 8px",
						}),
					}}
				/>
				{userOrigin?.value === "other" && (
					<Input
						placeholder="Please specify (optional)"
						value={otherOrigin}
						onChange={onOtherOriginChange}
						className={styles.otherInput}
						name="user_origin_other"
					/>
				)}
			</div>
		);
	}

	return (
		<>
			<div className={styles.greeting}>Welcome, {user.firstName || user.fullName}!</div>
			<div className={stylesOnboarding.title}>Tell us a little about yourself</div>

			{account.type === "PERSONAL" ? (
				<>
					{shouldAskOrigin && renderAskOrigin()}
					<div className={styles.formRow}>
						<label>Do you plan to use Fellow for work?</label>
						<Toggle checked={businessUse} onChange={onSignupForWorkChange} />
					</div>
				</>
			) : (
				<>
					{shouldAskOrigin && renderAskOrigin()}
					<div className={styles.formRow}>
						<label>Do you manage people{account && ` at ${account.name}`}?</label>
						<Toggle checked={isManager} onChange={onIsManagerChange} />
					</div>
					{!shouldAskOrigin && (
						<div className={cx(styles.formRow, { [styles.formRowDisabled]: !isManager })}>
							<label>How many people do you manage?</label>
							<Counter count={numReports} maximum={1000} onChange={onNumberOfReportsChange} />
						</div>
					)}
				</>
			)}

			<div className={styles.formRow}>
				<label>Please send me helpful product updates</label>
				<Toggle checked={emailOptin} onChange={onEmailOptinChange} />
			</div>

			<div className={styles.formRow}>
				<label>
					I agree to the{" "}
					<Link href="https://fellow.app/terms-of-use" target="_blank">
						Terms of Service
					</Link>{" "}
					and{" "}
					<Link href="https://fellow.app/privacy-policy" target="_blank">
						Privacy Policy
					</Link>
				</label>
				<Toggle checked={acceptedTermsOfService} onChange={onAcceptedTermsOfServiceChange} />
			</div>

			<div className={stylesOnboarding.footer}>
				{profileSaved ? (
					<div className={styles.syncText}>
						<FontAwesomeIcon spin={true} icon={faSpinnerThird} color="#596FFF" />
						<span>Hang on while we set up your calendar...</span>
					</div>
				) : (
					<Tooltip
						content={
							<div className={styles.tooltipContent}>
								You must agree to the Terms of Service and Privacy Policy to continue
							</div>
						}
						disabled={acceptedTermsOfService}
					>
						<div>
							<Button
								size="wide"
								variant="primary"
								disabled={!acceptedTermsOfService}
								onClick={saveProfile}
								className={cx(styles.syncButton)}
							>
								Continue
							</Button>
						</div>
					</Tooltip>
				)}
				<div className={stylesOnboarding.submessage}>
					<div>You can modify your profile later in user settings</div>
				</div>
			</div>
		</>
	);
}
